// The in-depth view of a place: a building drawn as a flat section cut (Manual of Section), with a room for each topic,
// scrolling sideways. Wheel, drag, swipe, arrow keys or the room bar move along it. The words come from the place's
// section of the text version in index.html, one room per heading.

const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

export interface Room { name: string; heading: string; x0: number; x1: number; lead?: boolean } // lead: the caption shows only the intro line
export interface SectionDef {
  id: string;
  width: number; // feet, along the cut
  height: number; // tallest point, feet
  rooms: Room[];
  draw: (k: Kit) => void;
  start?: number; // the room the view opens on
  fill?: [number, number]; // share of the window height the drawing spans (desktop, phone)
  ground?: [number, number]; // where the ground line sits, as a share of the window height (desktop, phone)
  capBottom?: boolean; // on phones, dock the caption at the bottom (for drawings that use the top)
  /** Extra controls or a game on top of the drawing; returns a clean-up. */
  mount?: (host: Host) => () => void;
  /** A tap on the drawing, in feet from the left and above the ground line; return true if it was handled. */
  onTap?: (wx: number, wy: number) => boolean;
}
export interface Host { box: HTMLElement; source: HTMLElement; go: (room: number) => void; room: () => number }
export interface Step { label: string; fn: () => void }
export interface SectionOpts {
  source: HTMLElement; // the place's section in the text version
  day: () => number; // 0 (night) to 1 (day)
  ground: () => string; // the landscape's ground colour, as the map shows it
  onRead: () => void;
  onClose: () => void;
  before?: Step; // the place before and after, reached by scrolling past the first or last room
  after?: Step;
}

// ---- drawing kit -------------------------------------------------------------
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const mixc = (a: number[], b: number[], t: number): number[] => a.map((v, i) => lerp(v, b[i], t));
const rgba = (c: number[], a = 1): string => `rgba(${Math.round(c[0])},${Math.round(c[1])},${Math.round(c[2])},${a})`;

export interface Colours {
  ink: string; inkSoft: string; paper: string; paperShade: string; poche: string; glass: string; interior: string;
  floor: string; leaf: string; leafDark: string; coral: string; lime: string; cobalt: string; yellow: string; ground: string;
  skyTop: string; skyBottom: string; tuft: string;
}
function palette(d: number, ground: string): Colours {
  const n = lerp(0.72, 1, d);
  const inkC = mixc([206, 214, 232], [20, 22, 30], d);
  const paperC = mixc([44, 52, 76], [244, 240, 228], d);
  return {
    ink: rgba(inkC), inkSoft: rgba(inkC, 0.4),
    paper: rgba(paperC), paperShade: rgba(mixc(paperC, inkC, 0.12)),
    poche: rgba(inkC),
    glass: rgba(mixc([90, 120, 160], [160, 204, 218], d), 0.34),
    interior: rgba(mixc([30, 40, 68], [230, 240, 240], d)),
    floor: rgba(mixc(paperC, [214, 208, 194], 0.6)),
    leaf: rgba(mixc([60, 96, 84], [168, 202, 120], d), 0.9),
    leafDark: rgba(mixc([44, 74, 66], [120, 160, 96], d), 0.9),
    coral: rgba([236 * n, 106 * n, 92 * n]), lime: rgba([170 * n, 210 * n, 60 * n]),
    cobalt: rgba([60 * n, 90 * n, 220 * n]), yellow: rgba([240 * n, 210 * n, 70 * n]),
    ground,
    skyTop: rgba(mixc([12, 20, 46], [176, 204, 228], d)), skyBottom: rgba(mixc([36, 46, 82], [240, 236, 222], d)),
    tuft: rgba(mixc([170, 180, 110], [249, 239, 137], d), 0.8),
  };
}

/** Draws in feet: x along the cut, y up from the ground line. */
export class Kit {
  ctx!: CanvasRenderingContext2D;
  s = 10; ox = 0; gy = 0; t = 0; dt = 0; w = 0; h = 0;
  c!: Colours;
  X(x: number): number { return this.ox + x * this.s; }
  Y(y: number): number { return this.gy - y * this.s; }
  /** Line weight in pixels: `n` is in drawing-weight units and thickens as the drawing is enlarged. */
  lw(n: number): number { return Math.max(0.7, n * this.s * 0.085); }
  private trace(p: number[], close: boolean): void {
    const g = this.ctx;
    g.beginPath();
    for (let i = 0; i < p.length; i += 2) { if (i) g.lineTo(this.X(p[i]), this.Y(p[i + 1])); else g.moveTo(this.X(p[i]), this.Y(p[i + 1])); }
    if (close) g.closePath();
  }
  /** Closed polygon: optional fill, optional outline (w > 0). */
  poly(p: number[], fill: string | null, w = 1): void {
    this.trace(p, true);
    if (fill) { this.ctx.fillStyle = fill; this.ctx.fill(); }
    if (w > 0) { this.ctx.strokeStyle = this.c.ink; this.ctx.lineWidth = this.lw(w); this.ctx.lineJoin = 'round'; this.ctx.stroke(); }
  }
  line(p: number[], w = 1, col?: string): void {
    this.trace(p, false);
    this.ctx.strokeStyle = col ?? this.c.ink; this.ctx.lineWidth = this.lw(w); this.ctx.lineCap = 'round'; this.ctx.stroke();
  }
  rect(x0: number, y0: number, x1: number, y1: number, fill: string | null, w = 1): void {
    this.poly([x0, y0, x1, y0, x1, y1, x0, y1], fill, w);
  }
  circle(x: number, y: number, r: number, fill: string | null, w = 1): void {
    const g = this.ctx;
    g.beginPath();
    g.arc(this.X(x), this.Y(y), r * this.s, 0, Math.PI * 2);
    if (fill) { g.fillStyle = fill; g.fill(); }
    if (w > 0) { g.strokeStyle = this.c.ink; g.lineWidth = this.lw(w); g.stroke(); }
  }
  /** A small standing figure, about 5.5 ft tall, for scale. */
  person(x: number, face = 1, y = 0): void {
    this.poly([x - 0.9, y, x + 0.9, y, x + 0.8, y + 3.4, x - 0.8, y + 3.4], this.c.paperShade, 0.8);
    this.poly([x - 0.9, y + 3.4, x + 0.9, y + 3.4, x + 0.7, y + 4.7, x - 0.7, y + 4.7], this.c.paper, 0.8);
    this.circle(x, y + 5.2, 0.62, this.c.paper, 0.8);
    this.circle(x + 0.3 * face, y + 5.25, 0.18, this.c.ink, 0);
  }
  text(str: string, x: number, y: number, px: number, align: CanvasTextAlign = 'left', col?: string): void {
    const g = this.ctx;
    g.font = `500 ${px}px ui-monospace, "SF Mono", Menlo, Consolas, monospace`;
    g.textAlign = align; g.textBaseline = 'alphabetic';
    g.fillStyle = col ?? this.c.ink;
    g.fillText(str, x, y);
  }
}

// ---- the view -------------------------------------------------------------------
let root: HTMLElement | null = null;
let stop: (() => void) | null = null;
export const isSectionOpen = (): boolean => root !== null;
export function closeSection(): void { stop?.(); }

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

/** The words under one heading of the text version. */
function roomText(source: HTMLElement, heading: string, lead = false): string {
  const h = Array.from(source.querySelectorAll('h3')).find((n) => (n.textContent ?? '').trim().toLowerCase() === heading.toLowerCase());
  const out: string[] = [];
  for (let n = h?.nextElementSibling ?? null; n && n.tagName !== 'H3'; n = n.nextElementSibling) {
    if (n.tagName === 'P') out.push((n.textContent ?? '').trim());
    else if (lead) break;
    else if (n.tagName === 'UL') n.querySelectorAll('li').forEach((li) => out.push((li.textContent ?? '').trim()));
  }
  return out.join(' ');
}

export function openSection(def: SectionDef, o: SectionOpts): void {
  closeSection();
  const opener = document.activeElement as HTMLElement | null;
  const rooms = def.rooms;
  const centre = (r: Room): number => (r.x0 + r.x1) / 2;
  const title = o.source.querySelector('h2')?.textContent ?? '';
  const role = o.source.dataset.role ?? '';

  const box = el('div', 'section');
  box.setAttribute('role', 'dialog');
  box.setAttribute('aria-modal', 'true');
  box.setAttribute('aria-label', `${title}, in section`);
  const cv = el('canvas');
  cv.setAttribute('aria-hidden', 'true');
  const back = el('button', 'sect-back', '‹ Back to the map');
  back.type = 'button';
  const cap = el('section', 'sect-caption');
  cap.setAttribute('aria-live', 'polite');
  const eyebrow = el('p', 'eyebrow');
  const h2 = el('h2');
  const body = el('p', 'body');
  const acts = el('div', 'actions');
  const prev = el('button', 'step prev'); prev.type = 'button';
  const read = el('button', 'open', 'Read more'); read.type = 'button';
  const next = el('button', 'step next'); next.type = 'button';
  acts.append(prev, read, next);
  cap.append(eyebrow, h2, body, acts);
  const bar = el('nav', 'sect-rooms');
  bar.setAttribute('aria-label', `Rooms of ${title}`);
  const list = el('ol');
  const chips = rooms.map((r, i) => {
    const li = el('li');
    const b = el('button', undefined, r.name); b.type = 'button';
    b.addEventListener('click', () => to(i));
    li.appendChild(b); list.appendChild(li);
    return b;
  });
  bar.appendChild(list);
  const hint = el('p', 'sect-hint', matchMedia('(pointer: coarse)').matches ? 'Swipe sideways' : 'Scroll or drag sideways');
  if (def.capBottom) { cap.classList.add('bottom'); box.classList.add('cap-bottom'); }
  box.append(cv, back, cap, hint, bar);
  document.body.appendChild(box);
  document.body.classList.add('paged');
  root = box;

  const g = cv.getContext('2d', { alpha: false }) as CanvasRenderingContext2D;
  const kit = new Kit();
  kit.ctx = g;
  let cam = centre(rooms[def.start ?? 0]);
  let target = cam;
  let shown = -1;
  let colKey = '';
  let raf = 0;
  let last = performance.now();
  const minX = centre(rooms[0]), maxX = centre(rooms[rooms.length - 1]);

  const nearest = (): number => {
    let b = 0;
    for (let i = 1; i < rooms.length; i++) if (Math.abs(centre(rooms[i]) - cam) < Math.abs(centre(rooms[b]) - cam)) b = i;
    return b;
  };
  const to = (i: number): void => { target = centre(rooms[Math.max(0, Math.min(rooms.length - 1, i))]); };
  const show = (i: number): void => {
    shown = i;
    const r = rooms[i];
    eyebrow.textContent = role;
    h2.textContent = r.name;
    body.textContent = roomText(o.source, r.heading, r.lead) || 'Coming soon.';
    chips.forEach((b, k) => (k === i ? b.setAttribute('aria-current', 'true') : b.removeAttribute('aria-current')));
    chips[i].scrollIntoView({ inline: 'center', block: 'nearest' });
    const p = i > 0 ? rooms[i - 1].name : o.before?.label;
    const n = i < rooms.length - 1 ? rooms[i + 1].name : o.after?.label;
    prev.hidden = !p; prev.textContent = p ? `‹ ${p}` : '';
    next.hidden = !n; next.textContent = n ? `${n} ›` : '';
    cap.classList.remove('swap');
    void cap.offsetWidth;
    if (!reduce) cap.classList.add('swap');
  };
  prev.addEventListener('click', () => { if (shown > 0) to(shown - 1); else if (o.before) { close(); o.before.fn(); } });
  next.addEventListener('click', () => { if (shown < rooms.length - 1) to(shown + 1); else if (o.after) { close(); o.after.fn(); } });
  read.addEventListener('click', () => { close(); o.onRead(); });

  const layout = (): void => {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = box.clientWidth, h = box.clientHeight;
    if (cv.width !== Math.round(w * dpr) || cv.height !== Math.round(h * dpr)) { cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr); }
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    kit.w = w; kit.h = h;
    const ph = w < 700 ? 1 : 0;
    kit.gy = h * (def.ground ? def.ground[ph] : ph ? 0.72 : 0.78);
    kit.s = Math.max(5, Math.min(16, (h * (def.fill ? def.fill[ph] : ph ? 0.42 : 0.44)) / def.height));
    kit.ox = w / 2 - cam * kit.s;
  };

  const frame = (now: number): void => {
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    cam = reduce ? target : cam + (target - cam) * (1 - Math.exp(-dt * 7));
    if (Math.abs(target - cam) < 0.005) cam = target;
    layout();
    const d = o.day();
    const ck = `${Math.round(d * 50)}|${o.ground()}`;
    if (ck !== colKey) { colKey = ck; kit.c = palette(d, o.ground()); }
    kit.t = now / 1000; kit.dt = dt;
    const c = kit.c;
    // sky, then the ground cut away below the line
    const sky = g.createLinearGradient(0, 0, 0, kit.gy);
    sky.addColorStop(0, c.skyTop); sky.addColorStop(1, c.skyBottom);
    g.fillStyle = sky; g.fillRect(0, 0, kit.w, kit.gy);
    g.fillStyle = c.ground; g.fillRect(0, kit.gy, kit.w, kit.h - kit.gy);
    g.strokeStyle = c.tuft; g.lineWidth = 1; g.globalAlpha = 0.5; g.beginPath();
    for (let y = kit.gy + 14; y < kit.h; y += 11) for (let x = ((y * 7) % 13) - 13; x < kit.w; x += 13) { g.moveTo(x, y); g.lineTo(x + 5, y - 6); }
    g.stroke(); g.globalAlpha = 1;
    def.draw(kit);
    // the ground line, in section
    g.strokeStyle = c.ink; g.lineWidth = kit.lw(1.4);
    g.beginPath(); g.moveTo(0, kit.gy); g.lineTo(kit.w, kit.gy); g.stroke();
    const i = nearest();
    if (i !== shown) show(i);
    raf = requestAnimationFrame(frame);
  };

  // input: wheel and drag move the cut sideways; a tap on a room's name goes there
  const clampT = (v: number): number => Math.max(minX - 14, Math.min(maxX + 14, v));
  box.addEventListener('wheel', (e) => { e.preventDefault(); target = clampT(target + (e.deltaX + e.deltaY) / kit.s * 0.9); }, { passive: false });
  let drag: { x: number; moved: number } | null = null;
  cv.addEventListener('pointerdown', (e) => { drag = { x: e.clientX, moved: 0 }; cv.setPointerCapture(e.pointerId); });
  cv.addEventListener('pointermove', (e) => {
    if (!drag) return;
    const dx = e.clientX - drag.x;
    drag.x = e.clientX; drag.moved += Math.abs(dx);
    target = clampT(target - dx / kit.s);
    cam = reduce ? target : cam - dx / kit.s;
  });
  cv.addEventListener('pointerup', (e) => {
    if (drag && drag.moved < 6) {
      const wx = (e.clientX - kit.ox) / kit.s;
      const k = rooms.findIndex((r) => wx >= r.x0 && wx < r.x1);
      if (!def.onTap?.(wx, (kit.gy - e.clientY) / kit.s) && k >= 0) to(k);
    }
    drag = null;
    target = Math.max(minX - 14, Math.min(maxX + 14, target));
  });
  cv.addEventListener('pointercancel', () => { drag = null; });
  const keys = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') { e.preventDefault(); close(); }
    else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); to(Math.min(rooms.length - 1, nearest() + 1)); }
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); to(Math.max(0, nearest() - 1)); }
    else if (e.key === 'Home') { e.preventDefault(); to(0); }
    else if (e.key === 'End') { e.preventDefault(); to(rooms.length - 1); }
  };
  addEventListener('keydown', keys);
  const unmount = def.mount?.({ box, source: o.source, go: to, room: nearest });
  back.addEventListener('click', () => close());
  back.focus({ preventScroll: true });

  let closed = false;
  function close(): void {
    if (closed) return;
    closed = true;
    cancelAnimationFrame(raf);
    removeEventListener('keydown', keys);
    unmount?.();
    box.remove();
    document.body.classList.remove('paged');
    root = null; stop = null;
    opener?.focus?.({ preventScroll: true });
    o.onClose();
  }
  stop = close;
  raf = requestAnimationFrame(frame);
}
